import type {
  OrphanedCustomerKind,
  OrphanedCustomerSortField,
} from '@travel-planner/shared/schemas/orphaned-customer';
import {
  orphanedCustomerKindValues,
  orphanedCustomerSortFields,
} from '@travel-planner/shared/schemas/orphaned-customer';

const sortOrderValues = ['asc', 'desc'] as const;
type SortOrder = (typeof sortOrderValues)[number];

export type OrphanedCustomersSearch = {
  itinerarySearch?: string;
  customerSearch?: string;
  consultantIds?: string[];
  // Distinct from an absent `consultantId`: explicitly filters to itineraries
  // with NO owning consultant.
  unassignedConsultant?: boolean;
  customerKind?: OrphanedCustomerKind;
  page?: number;
  pageSize?: number;
  sortBy?: OrphanedCustomerSortField;
  sortOrder?: SortOrder;
};

const asString = (v: unknown): string | undefined =>
  typeof v === 'string' && v.trim().length > 0 ? v : undefined;

const asIdArray = (v: unknown): string[] | undefined => {
  if (!Array.isArray(v)) return undefined;
  const ids = v.filter((x): x is string => typeof x === 'string');
  return ids.length > 0 ? ids : undefined;
};

const asKind = (v: unknown): OrphanedCustomerKind | undefined =>
  typeof v === 'string' &&
  (orphanedCustomerKindValues as readonly string[]).includes(v)
    ? (v as OrphanedCustomerKind)
    : undefined;

const asSortField = (v: unknown): OrphanedCustomerSortField | undefined =>
  typeof v === 'string' &&
  (orphanedCustomerSortFields as readonly string[]).includes(v)
    ? (v as OrphanedCustomerSortField)
    : undefined;

const asSortOrder = (v: unknown): SortOrder | undefined =>
  typeof v === 'string' && (sortOrderValues as readonly string[]).includes(v)
    ? (v as SortOrder)
    : undefined;

export const validateOrphanedCustomersSearch = (
  input: Record<string, unknown>
): OrphanedCustomersSearch => ({
  itinerarySearch: asString(input.itinerarySearch),
  customerSearch: asString(input.customerSearch),
  consultantIds: asIdArray(input.consultantIds),
  unassignedConsultant:
    input.unassignedConsultant === true || input.unassignedConsultant === 'true'
      ? true
      : undefined,
  customerKind: asKind(input.customerKind),
  page: typeof input.page === 'number' ? input.page : undefined,
  pageSize: typeof input.pageSize === 'number' ? input.pageSize : undefined,
  sortBy: asSortField(input.sortBy),
  sortOrder: asSortOrder(input.sortOrder),
});
